/**
 * Duel Route
 *
 * Manages the entire duel lifecycle with a persistent PixiJS application.
 * Handles:
 * - Persistent PixiJS App (never destroyed during duel)
 * - Phase management (Draft -> Battle)
 * - Background map rendering
 * - Overlay UI management
 * - Battle Replay
 */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Application, Container, Sprite, Assets } from "pixi.js";
import { useDuel } from "@/providers/duel-provider";
import { useAuth } from "@/providers/auth-provider";
import { useUser } from "@/providers/user-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { useDuelQueue } from "@/hooks/use-duel-queue";
import { DuelDraftScene } from "@/lib/duel/duel-draft-scene";
import { DuelDraftPixiOverlay } from "@/views/duel/duel-draft-pixi-overlay";
import { FindingOpponent } from "@/views/duel-queue/finding-opponent";
import { CompleteBattleLog } from "types/battle-engine";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { BattleReplay } from "@/lib/battle/BattleReplay";
import { Battle_Action } from "types/battle-engine/battle";
import { BattleStatsHUD } from "@/components/battle/BattleStatsHUD";
import {
  buildAssetManifest,
  preloadBattleAssets,
} from "@/lib/battle/asset-loader";
import { DuelResult } from "@/views/duel-queue/duel-result";
import { EnvironmentalEffects } from "@/components/environment/environmental-effects";
import { NightOverlay } from "@/components/environment/lighting-effects";
import { DuelHeader } from "@/views/duel-queue/duel-header";
import { QuitDuelDialog } from "@/components/ui/dialog/quit-duel-dialog";
import { BattleCompletedTransition } from "@/components/battle/BattleCompletedTransition";
import { PokemonBattleHUD } from "@/components/battle/PokemonBattleHUD";
import { KillAnnouncer, KillEvent } from "@/components/battle/KillAnnouncer";

type BattlePhase = "idle" | "loading" | "ready" | "playing" | "completed";

export const Duel = () => {
  const {
    duelState,
    queueStatus,
    cancelQueue,
    disconnectFromDuel,
    acknowledgeBattleEnd,
    quitDuel,
    connectToDuel,
    queuedTeam,
  } = useDuel();
  const { getAccessToken, state: authState } = useAuth();
  const { playerCountry, reloadProfile } = useUser();
  const user = authState.status === "authenticated" ? authState.user : null;
  const { setCurrentRoute } = useSPAContext();

  // Check for active duel from API (for reconnection)
  const {
    queueStatus: apiQueueStatus,
    currentDuelId,
    refetch: refetchQueue,
  } = useDuelQueue(true);

  // Auto-reconnect if we have an active duel but local state is idle
  useEffect(() => {
    const token = getAccessToken();
    const wsUrl = import.meta.env.VITE_WS_API_URL;

    // Only attempt reconnect if:
    // 1. Local state is idle (disconnected)
    // 2. API says we are in a duel or matched
    // 3. We have the duel ID and necessary credentials
    if (
      duelState.status === "idle" &&
      (apiQueueStatus === "MATCHED" || apiQueueStatus === "IN_DUEL") &&
      currentDuelId &&
      token &&
      user?.telegram_id &&
      wsUrl
    ) {
      console.log("[Duel] Auto-reconnecting to duel:", currentDuelId);
      connectToDuel(
        currentDuelId,
        user.telegram_id,
        token,
        wsUrl,
        true, // isReconnecting
      );
    }
  }, [
    duelState.status,
    apiQueueStatus,
    currentDuelId,
    user,
    getAccessToken,
    connectToDuel,
  ]);

  const isResultView = duelState.status === "finished";
  const isQueueView =
    queueStatus === "searching" ||
    queueStatus === "matched" ||
    duelState.status === "connecting" ||
    apiQueueStatus === "WAITING";
  const isGameView =
    !isResultView && !isQueueView && duelState.status !== "idle";

  const [showQuitDialog, setShowQuitDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Persistent PixiJS State
  const containerRef = useRef<HTMLDivElement>(null);
  const [app, setApp] = useState<Application | null>(null);
  const [world, setWorld] = useState<Container | null>(null);

  // Scenes
  const draftSceneRef = useRef<DuelDraftScene | null>(null);
  const [isDraftSceneReady, setIsDraftSceneReady] = useState(false);

  // Battle State
  const [pokemons, setPokemons] = useState<Record<string, ClientPokemon>>({});
  const [battleLog, setBattleLog] = useState<CompleteBattleLog | null>(null);
  const [battlePhase, setBattlePhase] = useState<BattlePhase>("idle");
  const [currentBattleUrl, setCurrentBattleUrl] = useState<string | null>(null);
  const hasStartedBattle = useRef(false);
  const replayRef = useRef<BattleReplay | null>(null);
  const [battleResult, setBattleResult] = useState<{
    isPlayerWinner: boolean;
    isDraw: boolean;
  } | null>(null);

  const [selectedPokemonId, setSelectedPokemonId] = useState<string | null>(
    null,
  );
  const [currentTick, setCurrentTick] = useState(0);
  const [killEvents, setKillEvents] = useState<KillEvent[]>([]);

  const pokemonIds = useMemo(() => Object.keys(pokemons), [pokemons]);

  // Initial selection
  useEffect(() => {
    if (!selectedPokemonId && pokemonIds.length > 0) {
      setSelectedPokemonId(pokemonIds[0] ?? null);
    }
  }, [pokemonIds, selectedPokemonId]);

  const handleNext = useCallback(() => {
    setSelectedPokemonId((prev) => {
      if (!prev) return pokemonIds[0] ?? null;
      const currentIndex = pokemonIds.indexOf(prev);
      const nextIndex = (currentIndex + 1) % pokemonIds.length;
      return pokemonIds[nextIndex] ?? null;
    });
  }, [pokemonIds]);

  const handlePrevious = useCallback(() => {
    setSelectedPokemonId((prev) => {
      if (!prev) return pokemonIds[pokemonIds.length - 1] ?? null;
      const currentIndex = pokemonIds.indexOf(prev);
      const prevIndex =
        (currentIndex - 1 + pokemonIds.length) % pokemonIds.length;
      return pokemonIds[prevIndex] ?? null;
    });
  }, [pokemonIds]);

  // Local score state - only update after battle completes
  const [displayedYourScore, setDisplayedYourScore] = useState(
    duelState.your_score || 0,
  );
  const [displayedOpponentScore, setDisplayedOpponentScore] = useState(
    duelState.opponent_score || 0,
  );

  // Sync local scores with duelState when not in battle
  useEffect(() => {
    if (battlePhase === "idle") {
      setDisplayedYourScore(duelState.your_score || 0);
      setDisplayedOpponentScore(duelState.opponent_score || 0);
    }
  }, [duelState.your_score, duelState.opponent_score, battlePhase]);

  // Initialization
  useEffect(() => {
    if (!isGameView) return;

    let localApp: Application | null = null;
    let isCancelled = false;

    const initApp = async () => {
      const gameContainer = containerRef.current;
      if (!gameContainer || isCancelled) return;
      if (gameContainer.querySelector("canvas")) return;

      try {
        localApp = new Application();

        try {
          await document.fonts?.load?.('16px "vck"');
        } catch {
          /* ignore */
        }

        if (isCancelled) return;

        await localApp.init({
          background: "#161e29",
          resizeTo: gameContainer,
          antialias: false,
          resolution: window.devicePixelRatio > 1 ? 2 : 1, // Limit resolution to max 2x for mobile performance
          autoDensity: true,
          powerPreference: "high-performance",
          hello: false, // Disable PixiJS console log
        });

        if (isCancelled) {
          localApp.destroy(true);
          return;
        }

        gameContainer.appendChild(localApp.canvas);
        localApp.stage.sortableChildren = true;

        const worldContainer = new Container();
        worldContainer.sortableChildren = true;
        localApp.stage.addChild(worldContainer);

        // Setup camera/scaling - fit map (480x960) to canvas, max scale 1
        const mapWidth = 480;
        const mapHeight = 960;
        const scaleX = gameContainer.clientWidth / mapWidth;
        const scaleY = gameContainer.clientHeight / mapHeight;
        const scale = Math.min(scaleX, scaleY, 1);

        worldContainer.scale.set(scale);
        worldContainer.position.set(
          (gameContainer.clientWidth - mapWidth * scale) / 2,
          (gameContainer.clientHeight - mapHeight * scale) / 2,
        );

        setApp(localApp);
        setWorld(worldContainer);
        console.log("[Duel] PixiJS app initialized");
      } catch (error) {
        console.error("[Duel] PixiJS init failed:", error);
      }
    };

    initApp();

    return () => {
      isCancelled = true;
      if (localApp) {
        try {
          localApp.ticker?.stop();
          localApp.canvas?.remove();
          localApp.stage?.destroy({ children: true, texture: true });
          localApp.destroy(true, { children: true, texture: true });
        } catch (e) {
          console.warn("[Duel] Cleanup error:", e);
        }
      }
      setApp(null);
      setWorld(null);
    };
  }, [isGameView]);

  // Handle Draft Scene Lifecycle
  useEffect(() => {
    // Only initialize draft scene if we are in draft/waiting phase AND not in battle
    const isDraftPhase =
      duelState.status === "waiting_for_step" ||
      duelState.status === "in_step" ||
      duelState.status === "waiting_for_opponent_pick" ||
      duelState.status === "step_completed" ||
      duelState.status === "new_round_starting";

    // Initialize Draft Scene
    if (
      isDraftPhase &&
      app &&
      world &&
      !draftSceneRef.current &&
      duelState.map_id
    ) {
      console.log(
        "[Duel] Initializing draft scene with map:",
        duelState.map_id,
      );

      // Clean world first just in case
      if (world.children.length > 0) {
        console.log("[Duel] Clearing world for draft scene");
        // We don't destroy children recursively here as it might break textures if managed elsewhere?
        // Actually, destroy({children: true}) is safe if we don't destroy textures
        while (world.children.length > 0) {
          const child = world.children[0];
          if (child) {
            world.removeChild(child);
            child.destroy({ children: true });
          } else {
            // Safety break to avoid infinite loop if child is somehow undefined
            world.removeChildren();
            break;
          }
        }
      }

      const scene = new DuelDraftScene(app, world);
      draftSceneRef.current = scene;

      const initScene = async () => {
        const isPlayerA = duelState.player_role === "player_a";
        const teamA = isPlayerA ? duelState.your_team : duelState.opponent_team;
        const teamB = isPlayerA ? duelState.opponent_team : duelState.your_team;

        await scene.initialize(duelState.map_id!, teamA, teamB);
        setIsDraftSceneReady(true);
      };

      initScene();
    }
  }, [
    app,
    world,
    duelState.status,
    duelState.map_id,
    duelState.your_team,
    duelState.opponent_team,
    duelState.player_role,
  ]);

  // Battle Logic

  // 1. Fetch Battle Log
  useEffect(() => {
    if (
      (duelState.status === "battle_starting" ||
        duelState.status === "in_battle") &&
      duelState.current_battle?.battle_log_url &&
      duelState.current_battle.battle_log_url !== currentBattleUrl
    ) {
      const fetchLog = async () => {
        setCurrentBattleUrl(duelState.current_battle!.battle_log_url);
        setBattlePhase("loading");
        hasStartedBattle.current = false;
        setPokemons({});
        setBattleLog(null);
        setKillEvents([]);

        try {
          const res = await fetch(duelState.current_battle!.battle_log_url);
          const log = (await res.json()) as CompleteBattleLog;
          setBattleLog(log);
        } catch (e) {
          console.error("[Duel] Failed to fetch battle log", e);
        }
      };
      fetchLog();
    }
  }, [duelState.status, duelState.current_battle, currentBattleUrl]);

  // 2. Setup Battle (Reuse Draft Scene entities)
  useEffect(() => {
    if (battleLog && app && world && !hasStartedBattle.current) {
      hasStartedBattle.current = true;

      const setupBattle = async () => {
        console.log("[Duel] Setting up battle from draft scene...");

        // Use continuity from Draft Scene if available
        if (draftSceneRef.current) {
          const existingPokemons =
            draftSceneRef.current.prepareForBattle(battleLog);

          // Update existing pokemons with battle data (skills, stats, etc.)
          Object.values(existingPokemons).forEach((pokemon) => {
            const member = [
              ...battleLog.teamA.members,
              ...battleLog.teamB.members,
            ].find((m) => m.battleData.id === pokemon.id);

            if (member) {
              pokemon.updateBattleData(
                member.battleData,
                member.initialPosition,
              );
            }
          });

          // Preload any additional assets
          const manifest = buildAssetManifest(
            battleLog.teamA.members.map((m) => m.pokemon),
            battleLog.teamB.members.map((m) => m.pokemon),
          );
          await preloadBattleAssets(manifest, () => {});

          setPokemons(existingPokemons);
          setBattlePhase("ready");
          setSelectedPokemonId(null); // Reset selection

          // Setup interaction for existing pokemons
          Object.values(existingPokemons).forEach((p) => {
            p.setOnClick((clickedPokemon) => {
              console.log("Clicked pokemon:", clickedPokemon.id);
              setSelectedPokemonId(clickedPokemon.id.toString());
            });
          });

          // Clean up draft scene reference but keep the instance alive implicitly via World?
          // No, we need to let go of the ref so we don't try to reuse it for NEXT draft round improperly
          // But we can't destroy it yet.
          // We'll destroy it when battle ends and we go back to draft (where we'll create a NEW scene).
          draftSceneRef.current = null;
          setIsDraftSceneReady(false);
        } else {
          // Fallback: Full setup if no draft scene (e.g. refresh)
          console.log("[Duel] Full battle setup (fallback)");
          // Clean world
          if (world) {
            while (world.children.length > 0) {
              const child = world.children[0];
              if (child) {
                world.removeChild(child);
                child.destroy({ children: true });
              } else {
                world.removeChildren();
                break;
              }
            }
          }

          // Load map
          // ... (simplified fallback logic similar to Raid.tsx or just show error/loading)
          // For now assuming normal flow
        }
      };

      setupBattle();
    }
  }, [battleLog, app, world]);

  // 3. Play Battle - Transition to Playing
  useEffect(() => {
    if (
      battlePhase === "ready" &&
      battleLog &&
      Object.keys(pokemons).length > 0
    ) {
      const timer = setTimeout(() => {
        setBattlePhase("playing");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [battlePhase, battleLog, pokemons]);

  // 4. Play Battle - Execute Replay
  useEffect(() => {
    if (
      battlePhase === "playing" &&
      battleLog &&
      Object.keys(pokemons).length > 0
    ) {
      // Prevent duplicate replay if effect re-runs while playing
      if (replayRef.current) return;

      let isCancelled = false;

      const play = async () => {
        setKillEvents([]);
        const routines: Record<string, Battle_Action[]> = {};
        [...battleLog.teamA.members, ...battleLog.teamB.members].forEach(
          (m) => {
            routines[m.pokemon.id] = m.actions;
          },
        );

        let lastTickUpdate = 0;
        const replay = new BattleReplay(
          pokemons,
          (tick) => {
            // Throttle tick updates to React state (e.g. every 100ms)
            const now = Date.now();
            if (now - lastTickUpdate > 100) {
              setCurrentTick(tick);
              lastTickUpdate = now;
            }
          },
          (event) => {
            if (event.event_type === "KILL") {
              const killer = pokemons[event.killer_id];
              const victim = pokemons[event.victim_id];

              if (killer && victim) {
                const newKillEvent: KillEvent = {
                  id: `${event.tick}-${event.killer_id}-${event.victim_id}`,
                  killerName: killer.Name,
                  victimName: victim.Name,
                  killerTeam: killer.Team,
                  victimTeam: victim.Team,
                  killerBaseName: killer.base_pokemon_name,
                  victimBaseName: victim.base_pokemon_name,
                };
                setKillEvents((prev) => [...prev, newKillEvent]);
              }
            }
          },
        );
        replayRef.current = replay;

        try {
          await replay.play(
            routines,
            battleLog.statsPerTick,
            battleLog.events,
            battleLog.mana,
          );
        } catch (e) {
          console.error("[Duel] Battle replay error:", e);
        } finally {
          if (replayRef.current === replay) {
            replayRef.current = null;
          }
        }

        if (isCancelled) return;

        // Determine winner
        const isPlayerA = duelState.player_role === "player_a";
        const winner = battleLog.winner;

        let playerWon = false;
        let isDraw = false;
        if (winner === "teamA") {
          playerWon = isPlayerA;
        } else if (winner === "teamB") {
          playerWon = !isPlayerA;
        } else {
          isDraw = true;
        }

        setBattleResult({ isPlayerWinner: playerWon, isDraw });
        setBattlePhase("completed");

        acknowledgeBattleEnd();
      };

      play();

      return () => {
        isCancelled = true;
        if (replayRef.current) {
          replayRef.current.destroy();
          replayRef.current = null;
        }
      };
    }
  }, [battlePhase, battleLog, pokemons, acknowledgeBattleEnd]);

  const handleCancel = async () => {
    if (isCancelling) return; // Prevent multiple clicks

    setIsCancelling(true);
    try {
      const token = getAccessToken();
      // Only cancel queue if we're still searching - don't call quitDuel for finished duels
      if (token && duelState.status !== "finished") {
        await cancelQueue(token);
        // Refetch queue status to ensure UI updates immediately
        void refetchQueue();
      }

      // If the duel was finished, reload profile to get latest rewards
      if (duelState.status === "finished") {
        void reloadProfile();
      }

      disconnectFromDuel();
      setCurrentRoute(undefined);
    } finally {
      setIsCancelling(false);
    }
  };

  const handlePanicQuit = async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      const token = getAccessToken();
      if (token) {
        await quitDuel(token);
      }
    } finally {
      // Always disconnect and go home on panic quit
      disconnectFromDuel();
      setCurrentRoute(undefined);
      setIsCancelling(false);
    }
  };

  // Render Logic

  // 1. Result Screen
  if (duelState.status === "finished") {
    return <DuelResult duelState={duelState} onContinue={handleCancel} />;
  }

  // 2. Error Screen
  if (duelState.status === "error") {
    return (
      <div className="w-full h-full bg-[#161e29] flex flex-col items-center justify-center gap-6 p-8">
        <h2 className="text-2xl text-red-400 font-bold tracking-widest uppercase">
          Connection Failed
        </h2>
        <p className="text-gray-300 text-center">
          {duelState.error?.message || "Unable to connect to duel server."}
        </p>
        <div className="flex flex-col gap-4">
          <button
            onClick={handleCancel}
            className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold tracking-wider transition-colors"
          >
            RETURN TO MENU
          </button>
          <button
            onClick={handlePanicQuit}
            className="px-8 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold tracking-wider transition-colors text-sm"
          >
            PANIC QUIT DUEL
          </button>
        </div>
      </div>
    );
  }

  // 3. Queue / Searching
  if (
    queueStatus === "searching" ||
    queueStatus === "matched" ||
    duelState.status === "connecting" ||
    apiQueueStatus === "WAITING"
  ) {
    return (
      <section className="w-full h-full bg-pk-gray-1 relative overflow-hidden py-12">
        <FindingOpponent
          handleCancel={handleCancel}
          countryCode={duelState.opponent_player?.country ?? null}
          isCancelling={isCancelling}
          team={
            duelState.your_team?.length ? duelState.your_team : queuedTeam ?? []
          }
        />
      </section>
    );
  }

  // 3. Main Duel View
  return (
    <div className="flex flex-col h-full w-full bg-[#161e29] overflow-hidden relative">
      {/* PixiJS Container */}
      <div
        ref={containerRef}
        className="absolute inset-0 z-0"
        data-duel-canvas
      />

      <EnvironmentalEffects weather={"clouds"} className="opacity-0" />
      <NightOverlay opacity={0.07} />

      {/* Top Header - Only visible during draft phase */}
      {isGameView && battlePhase === "idle" && (
        <div className="pointer-events-auto w-full z-50">
          <DuelHeader
            round={duelState.current_round_index || 0}
            yourScore={displayedYourScore}
            opponentScore={displayedOpponentScore}
            yourName={user?.username || "You"}
            opponentName={duelState.opponent_player?.username || "Opponent"}
            yourCountry={playerCountry ?? null}
            opponentCountry={duelState.opponent_player?.country ?? null}
            isPlayerA={duelState.player_role === "player_a"}
            onQuit={() => setShowQuitDialog(true)}
          />
        </div>
      )}

      {/* Quit Dialog */}
      <div className="pointer-events-auto z-[200]">
        <QuitDuelDialog
          isOpen={showQuitDialog}
          onClose={() => setShowQuitDialog(false)}
          onConfirm={async () => {
            const token = getAccessToken();
            if (token) {
              await quitDuel(token);
            }
            setShowQuitDialog(false);
          }}
        />
      </div>

      {/* UI Overlays */}
      <div className="relative z-10 w-full h-full pointer-events-none flex flex-col pt-16">
        {/* Battle Transition */}
        {battlePhase === "completed" && battleResult && (
          <div className="pointer-events-auto absolute inset-0 z-[100]">
            <BattleCompletedTransition
              winnerName={
                battleResult.isDraw
                  ? ""
                  : battleResult.isPlayerWinner
                  ? user?.username || "You"
                  : duelState.opponent_player?.username || "Opponent"
              }
              playerScore={displayedYourScore}
              opponentScore={displayedOpponentScore}
              isPlayerWinner={battleResult.isPlayerWinner}
              isDraw={battleResult.isDraw}
              onReady={() => {
                setBattlePhase("idle");
                setPokemons({});
                setBattleLog(null);
                setBattleResult(null);
                hasStartedBattle.current = false;
                setKillEvents([]);

                // Sync scores in case they updated in background
                setDisplayedYourScore(duelState.your_score || 0);
                setDisplayedOpponentScore(duelState.opponent_score || 0);
              }}
            />
          </div>
        )}

        {/* Battle HUD */}
        {duelState.status === "in_battle" &&
          (battlePhase === "playing" || battlePhase === "ready") &&
          battleLog && (
            <>
              <KillAnnouncer events={killEvents} />

              {/* Legacy HUD (keep for now or remove if desired, but hiding when detailed HUD is open) */}
              <BattleStatsHUD
                battleState={pokemons}
                battleStats={battleLog.statsPerTick}
                currentTick={currentTick}
              />

              {/* Selected Pokemon Detailed HUD */}
              {selectedPokemonId && pokemons[selectedPokemonId] && (
                <PokemonBattleHUD
                  pokemon={pokemons[selectedPokemonId]}
                  battleLog={battleLog}
                  currentTick={currentTick}
                  onClose={() => setSelectedPokemonId(null)}
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                />
              )}
            </>
          )}

        {/* Draft UI */}
        {(duelState.status === "waiting_for_step" ||
          duelState.status === "in_step" ||
          duelState.status === "waiting_for_opponent_pick" ||
          duelState.status === "step_completed" ||
          duelState.status === "battle_starting") && (
          <DuelDraftPixiOverlay
            isVisible={
              isDraftSceneReady || duelState.status === "battle_starting"
            }
            draftScene={draftSceneRef.current}
          />
        )}
      </div>
    </div>
  );
};
